import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Calendar as CalendarIcon, Clock, Plus, FileText, CheckCircle, XCircle, AlertCircle,
  ChevronLeft, ChevronRight, Users, Gift
} from "lucide-react";
import {
  format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday,
  isSameDay, addMonths, subMonths, startOfWeek, endOfWeek, parseISO, isWithinInterval
} from "date-fns";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { cn, parseDateLocal } from "@/lib/utils";

// ---------- Types for new time-off system ----------

interface TimeOffPolicy {
  id: number;
  name: string;
  code: string | null;
  policyType: string | null;
  color: string | null;
  accrualUnit: string;
  requiresApproval: boolean | null;
  maxConsecutiveDays: number | null;
  advanceNoticeDays: number | null;
  allowNegativeBalance: boolean | null;
  maxBalance: string | null;
}

interface TimeOffBalanceItem {
  policyId: number;
  policyName: string;
  policyType: string | null;
  color: string | null;
  accrualUnit: string;
  allocated: number;
  used: number;
  pending: number;
  available: number;
}

interface TimeOffRequestItem {
  id: number;
  source: "new" | "legacy";
  policyId: number | null;
  policyName: string;
  policyType: string;
  policyColor: string | null;
  startDate: string;
  endDate: string;
  totalHours: number;
  requestType: string;
  status: string;
  reason: string | null;
  denialReason: string | null;
  notes: string | null;
  submittedDate: string;
  reviewedBy: string | null;
  reviewedAt: string | null;
}

interface CompanyHoliday {
  id: string;
  name: string;
  date: string;
  paidHoliday: boolean;
  recurring: boolean;
}

// ---------- Default fallback colors ----------
const DEFAULT_POLICY_COLORS: Record<string, string> = {
  vacation: "#10b981",
  sick: "#3b82f6",
  personal: "#8b5cf6",
  pto: "#f59e0b",
  bereavement: "#6b7280",
  jury_duty: "#6b7280",
  fmla: "#ef4444",
  parental: "#ec4899",
  military: "#14b8a6",
  unpaid: "#9ca3af",
  other: "#6b7280",
};

function getPolicyColor(policy: { color?: string | null; policyType?: string | null }): string {
  return policy.color || DEFAULT_POLICY_COLORS[policy.policyType || ""] || "#6b7280";
}

// ---------- Calendar component ----------

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function PTOCalendar({
  requests,
  holidays = [],
  policies = [],
}: {
  requests: TimeOffRequestItem[];
  holidays?: CompanyHoliday[];
  policies?: TimeOffPolicy[];
}) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);

  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const calendarStart = startOfWeek(monthStart);
    const calendarEnd = endOfWeek(monthEnd);
    return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  }, [currentMonth]);

  const getRequestsForDay = (day: Date) => {
    return requests.filter((req) => {
      const startDate = parseISO(req.startDate);
      const endDate = parseISO(req.endDate);
      return isWithinInterval(day, { start: startDate, end: endDate }) ||
             isSameDay(day, startDate) ||
             isSameDay(day, endDate);
    });
  };

  const getHolidayForDay = (day: Date) => {
    return holidays.find((holiday) => {
      const holidayDate = parseISO(holiday.date);
      return isSameDay(day, holidayDate);
    });
  };

  const selectedDayRequests = selectedDay ? getRequestsForDay(selectedDay) : [];
  const selectedDayHoliday = selectedDay ? getHolidayForDay(selectedDay) : null;

  const getRequestBgColor = (req: TimeOffRequestItem) => {
    const color = req.policyColor || getPolicyColor({ policyType: req.policyType });
    return color;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">{format(currentMonth, "MMMM yyyy")}</h3>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => setCurrentMonth(new Date())}>
            Today
          </Button>
          <Button variant="outline" size="sm" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-px bg-muted rounded-lg overflow-hidden">
        {WEEKDAYS.map((day) => (
          <div key={day} className="bg-muted/50 py-2 text-center text-sm font-medium text-muted-foreground">
            {day}
          </div>
        ))}
        {calendarDays.map((day) => {
          const dayRequests = getRequestsForDay(day);
          const dayHoliday = getHolidayForDay(day);
          const isCurrentMonth = isSameMonth(day, currentMonth);
          const isSelected = selectedDay && isSameDay(day, selectedDay);

          return (
            <div
              key={day.toISOString()}
              className={cn(
                "min-h-[80px] p-1 bg-background border-t cursor-pointer transition-colors",
                !isCurrentMonth && "bg-muted/30 text-muted-foreground",
                isToday(day) && "bg-blue-50 dark:bg-blue-950",
                dayHoliday && "bg-red-50 dark:bg-red-950/30",
                isSelected && "ring-2 ring-primary"
              )}
              onClick={() => setSelectedDay(day)}
            >
              <div className={cn(
                "text-sm font-medium mb-1 flex items-center gap-1",
                isToday(day) && "text-blue-600 dark:text-blue-400",
                dayHoliday && "text-red-600 dark:text-red-400"
              )}>
                {format(day, "d")}
                {dayHoliday && <Gift className="h-3 w-3" />}
              </div>
              <div className="space-y-1">
                {dayHoliday && (
                  <div className="text-xs px-1 py-0.5 rounded truncate bg-red-500 text-white">
                    {dayHoliday.name}
                  </div>
                )}
                {dayRequests.slice(0, dayHoliday ? 1 : 2).map((req) => (
                  <div
                    key={req.id}
                    className="text-xs px-1 py-0.5 rounded truncate text-white"
                    style={{ backgroundColor: getRequestBgColor(req) }}
                  >
                    {req.policyName}
                  </div>
                ))}
                {dayRequests.length > (dayHoliday ? 1 : 2) && (
                  <div className="text-xs text-muted-foreground">+{dayRequests.length - (dayHoliday ? 1 : 2)} more</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {selectedDay && (
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-base">
              {format(selectedDay, "EEEE, MMMM d, yyyy")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {selectedDayHoliday && (
              <div className="flex items-center justify-between p-3 mb-3 border rounded bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800">
                <div className="flex items-center gap-2">
                  <Gift className="h-5 w-5 text-red-600" />
                  <div>
                    <span className="font-medium text-red-900 dark:text-red-100">{selectedDayHoliday.name}</span>
                    <p className="text-xs text-red-600 dark:text-red-400">
                      {selectedDayHoliday.paidHoliday ? "Paid Company Holiday" : "Company Holiday"}
                    </p>
                  </div>
                </div>
                <Badge className="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">Holiday</Badge>
              </div>
            )}
            {selectedDayRequests.length > 0 ? (
              <div className="space-y-2">
                {selectedDayRequests.map((req) => (
                  <div key={req.id} className="flex items-center justify-between p-2 border rounded">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: getRequestBgColor(req) }} />
                      <span className="font-medium">{req.policyName}</span>
                    </div>
                    <Badge variant={req.status === 'approved' ? 'default' : req.status === 'pending' ? 'secondary' : 'destructive'}>
                      {req.status}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : !selectedDayHoliday ? (
              <p className="text-sm text-muted-foreground">No time off scheduled for this day</p>
            ) : null}
          </CardContent>
        </Card>
      )}

      {/* Dynamic legend from policies */}
      <div className="flex items-center gap-4 text-sm flex-wrap">
        {policies.map(p => (
          <div key={p.id} className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: getPolicyColor(p) }} />
            <span>{p.name}</span>
          </div>
        ))}
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-red-500" />
          <span>Company Holiday</span>
        </div>
      </div>
    </div>
  );
}

// ---------- Main PTO Page ----------

export default function PTO() {
  const [activeTab, setActiveTab] = useState("balances");
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [selectedPolicyId, setSelectedPolicyId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reason, setReason] = useState("");
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch policies from new system
  const { data: policies = [] } = useQuery<TimeOffPolicy[]>({
    queryKey: ["/api/time-off/employee/policies"],
  });

  // Fetch balances from new system (null = no policies configured)
  const { data: balances } = useQuery<TimeOffBalanceItem[] | null>({
    queryKey: ["/api/time-off/employee/balances"],
  });

  // Fetch requests from new system (merged new + legacy)
  const { data: requests = [] } = useQuery<TimeOffRequestItem[]>({
    queryKey: ["/api/time-off/employee/requests"],
  });

  const currentYear = new Date().getFullYear();
  const { data: holidays = [] } = useQuery<CompanyHoliday[]>({
    queryKey: ["/api/time-off/holidays", { year: currentYear.toString() }],
  });

  const cancelMutation = useMutation({
    mutationFn: async (requestId: number) => {
      return apiRequest("PATCH", `/api/time-off/requests/${requestId}/cancel`);
    },
    onSuccess: () => {
      toast({
        title: "Request Cancelled",
        description: "Your time off request has been cancelled.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/time-off/employee/requests"] });
      queryClient.invalidateQueries({ queryKey: ["/api/time-off/employee/balances"] });
    },
    onError: (error: Error) => {
      toast({
        title: "Cancellation Failed",
        description: error.message || "Failed to cancel request. Please try again.",
        variant: "destructive",
      });
    },
  });

  const submitMutation = useMutation({
    mutationFn: async (requestData: any) => {
      return apiRequest("POST", "/api/time-off/employee/requests", requestData);
    },
    onSuccess: () => {
      toast({
        title: "Time Off Request Submitted",
        description: "Your request has been submitted for approval.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/time-off/employee/requests"] });
      queryClient.invalidateQueries({ queryKey: ["/api/time-off/employee/balances"] });
      setShowRequestForm(false);
      setSelectedPolicyId("");
      setStartDate("");
      setEndDate("");
      setReason("");
    },
    onError: (error: Error) => {
      toast({
        title: "Request Failed",
        description: error.message || "Failed to submit time off request. Please try again.",
        variant: "destructive",
      });
    },
  });

  const selectedPolicy = policies.find(p => String(p.id) === selectedPolicyId);

  const calculateDays = () => {
    if (!startDate || !endDate) return 0;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  };

  const checkHolidayOverlap = (start: string, end: string): CompanyHoliday[] => {
    const startD = parseISO(start);
    const endD = parseISO(end);
    return holidays.filter((holiday) => {
      const holidayDate = parseISO(holiday.date);
      return isWithinInterval(holidayDate, { start: startD, end: endD }) ||
             isSameDay(holidayDate, startD) ||
             isSameDay(holidayDate, endD);
    });
  };

  const handleSubmitRequest = () => {
    if (!selectedPolicyId || !startDate || !endDate || !reason) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      return;
    }

    const overlappingHolidays = checkHolidayOverlap(startDate, endDate);
    if (overlappingHolidays.length > 0) {
      const holidayNames = overlappingHolidays.map(h => h.name).join(", ");
      toast({
        title: "Request Overlaps with Company Holiday",
        description: `Your request includes the following company holiday(s): ${holidayNames}. You don't need to use PTO on paid holidays - please adjust your dates.`,
        variant: "destructive",
      });
      return;
    }

    const days = calculateDays();
    const hours = selectedPolicy?.accrualUnit === "days" ? days : days * 8;

    submitMutation.mutate({
      policyId: parseInt(selectedPolicyId),
      startDate,
      endDate,
      totalHours: hours,
      requestType: days > 1 ? "multi_day" : "full_day",
      reason,
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "approved":
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case "denied":
        return <XCircle className="h-4 w-4 text-red-500" />;
      case "pending":
        return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "approved":
        return <Badge variant="default" className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">Approved</Badge>;
      case "denied":
        return <Badge variant="destructive">Denied</Badge>;
      case "pending":
        return <Badge variant="secondary" className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">Pending</Badge>;
      case "cancelled":
        return <Badge variant="outline">Cancelled</Badge>;
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  const pendingRequests = requests.filter(r => r.status === "pending");
  const approvedRequests = requests.filter(r => r.status === "approved");

  // Compute totals from balance items
  const totalBalance = useMemo(() => {
    if (!balances || balances.length === 0) return null;
    return balances.reduce(
      (acc, b) => ({
        allocated: acc.allocated + b.allocated,
        used: acc.used + b.used,
        pending: acc.pending + b.pending,
        available: acc.available + b.available,
      }),
      { allocated: 0, used: 0, pending: 0, available: 0 }
    );
  }, [balances]);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Time Off</h1>
          <p className="text-muted-foreground">Manage your time off requests and view your balances</p>
        </div>
        <Button onClick={() => {
          if (policies.length === 0) {
            toast({
              title: "No time off policies available",
              description: "Your company hasn't set up time off policies yet. Contact your HR administrator.",
              variant: "destructive",
            });
            return;
          }
          setShowRequestForm(true);
        }} className="gap-2">
          <Plus className="h-4 w-4" />
          Request Time Off
        </Button>
      </div>

      {showRequestForm && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5" />
              Submit Time Off Request
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="requestType">Request Type</Label>
                <Select value={selectedPolicyId} onValueChange={setSelectedPolicyId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {policies.map(p => (
                      <SelectItem key={p.id} value={String(p.id)}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="startDate">Start Date</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>

              <div>
                <Label htmlFor="endDate">End Date</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </div>

            {startDate && endDate && (
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-sm">
                  <strong>Total Days:</strong> {calculateDays()} day{calculateDays() !== 1 ? "s" : ""}
                  {selectedPolicy && (
                    <span className="text-muted-foreground ml-2">
                      ({selectedPolicy.accrualUnit === "days" ? calculateDays() : calculateDays() * 8} {selectedPolicy.accrualUnit})
                    </span>
                  )}
                </p>
              </div>
            )}

            <div>
              <Label htmlFor="reason">Reason</Label>
              <Textarea
                placeholder="Please provide a brief reason for your time off request..."
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </div>

            <div className="flex gap-2">
              <Button onClick={handleSubmitRequest} disabled={submitMutation.isPending}>
                {submitMutation.isPending ? "Submitting..." : "Submit Request"}
              </Button>
              <Button variant="outline" onClick={() => setShowRequestForm(false)}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="balances" className="gap-2">
            <Clock className="h-4 w-4" />
            Balances
          </TabsTrigger>
          <TabsTrigger value="requests" className="gap-2">
            <FileText className="h-4 w-4" />
            My Requests
            {pendingRequests.length > 0 && (
              <Badge variant="secondary" className="ml-1">{pendingRequests.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="calendar" className="gap-2">
            <CalendarIcon className="h-4 w-4" />
            Calendar
          </TabsTrigger>
        </TabsList>

        <TabsContent value="balances" className="space-y-4">
          {balances === null || balances === undefined ? (
            <Card>
              <CardContent className="py-12">
                <div className="text-center">
                  <Clock className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                  <h3 className="text-lg font-semibold mb-2">No Time Off Policies Configured</h3>
                  <p className="text-muted-foreground">
                    Your company has not set up time off policies yet. Please contact your HR administrator for more information.
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {balances.map((bal) => {
                const color = getPolicyColor({ color: bal.color, policyType: bal.policyType });
                return (
                  <Card key={bal.policyId} className="border-l-4" style={{ borderLeftColor: color }}>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <div className="w-4 h-4 rounded-full" style={{ backgroundColor: color }} />
                        {bal.policyName}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold" style={{ color }}>
                        {bal.available}
                      </div>
                      <p className="text-xs text-muted-foreground">available {bal.accrualUnit}</p>
                      <div className="mt-3 space-y-1 text-xs">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Allocated:</span>
                          <span className="font-medium">{bal.allocated}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Used:</span>
                          <span className="font-medium">{bal.used}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Pending:</span>
                          <span className="font-medium">{bal.pending}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}

              {/* Total balance card */}
              {totalBalance && balances.length > 1 && (
                <Card className="border-l-4 border-l-orange-500">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <FileText className="h-4 w-4 text-orange-500" />
                      Total Balance
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-orange-600 dark:text-orange-400">
                      {totalBalance.available}
                    </div>
                    <p className="text-xs text-muted-foreground">available total</p>
                    <div className="mt-3 space-y-1 text-xs">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Allocated:</span>
                        <span className="font-medium">{totalBalance.allocated}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Used:</span>
                        <span className="font-medium">{totalBalance.used}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Pending:</span>
                        <span className="font-medium">{totalBalance.pending}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="requests" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                My Time Off Requests
              </CardTitle>
              <CardDescription>
                {requests.length} total requests • {pendingRequests.length} pending • {approvedRequests.length} approved
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {requests.map((request) => {
                  const color = request.policyColor || getPolicyColor({ policyType: request.policyType });
                  const days = request.totalHours / 8;
                  const displayDays = Math.round(days * 10) / 10;

                  return (
                    <div
                      key={`${request.source}-${request.id}`}
                      className="flex items-center justify-between p-4 border-l-4 border rounded-lg hover:bg-muted/25 transition-colors"
                      style={{ borderLeftColor: color }}
                    >
                      <div className="flex items-center gap-4">
                        {getStatusIcon(request.status)}
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{request.policyName}</p>
                            {getStatusBadge(request.status)}
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {format(parseDateLocal(request.startDate) ?? new Date(request.startDate), "MMM d")} - {format(parseDateLocal(request.endDate) ?? new Date(request.endDate), "MMM d, yyyy")}
                            ({displayDays} day{displayDays !== 1 ? 's' : ''})
                          </p>
                          {request.reason && (
                            <p className="text-sm text-muted-foreground">{request.reason}</p>
                          )}
                          {request.denialReason && (
                            <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                              Denial reason: {request.denialReason}
                            </p>
                          )}
                          {request.notes && (
                            <p className="text-sm text-muted-foreground mt-1">
                              Note: {request.notes}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right text-sm text-muted-foreground">
                          {request.submittedDate && (
                            <p>Submitted: {format(new Date(request.submittedDate), "MMM d, yyyy")}</p>
                          )}
                          {request.reviewedBy && (
                            <p>{request.status === "denied" ? "Denied" : "Reviewed"} by: {request.reviewedBy}</p>
                          )}
                        </div>
                        {request.status === "pending" && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30"
                            onClick={() => cancelMutation.mutate(request.id)}
                            disabled={cancelMutation.isPending}
                          >
                            <XCircle className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}

                {requests.length === 0 && (
                  <div className="text-center py-12">
                    <CalendarIcon className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                    <p className="text-muted-foreground">No time off requests found</p>
                    <Button variant="outline" className="mt-4" onClick={() => {
                      if (policies.length === 0) {
                        toast({
                          title: "No time off policies available",
                          description: "Your company hasn't set up time off policies yet. Contact your HR administrator.",
                          variant: "destructive",
                        });
                        return;
                      }
                      setShowRequestForm(true);
                    }}>
                      <Plus className="h-4 w-4 mr-2" />
                      Submit Your First Request
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="calendar" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CalendarIcon className="h-5 w-5" />
                My Time Off Calendar
              </CardTitle>
              <CardDescription>
                Visual overview of your scheduled time off
              </CardDescription>
            </CardHeader>
            <CardContent>
              <PTOCalendar requests={requests} holidays={holidays} policies={policies} />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
